import React from 'react';
import cn from 'classnames';

import { IS_PROD } from 'utils/environment';
import { useResponsiveContext } from 'client/components/common/ResponsiveProvider/Context';
import { DEFAULT_MEDIA_QUERY_PLATFORMS } from 'src/common/constants';
import { CONTAINER_ID, CONTAINER_WRAP_ID, CONTAINER_POSITION_TOP_OFFSET_EL_ID } from './constants';
import { useOutbrain } from './use-outbrain';
import css from './OutbrainWidget.scss';

const { MOBILE, MOBILE_LANDSCAPE, TABLET } = DEFAULT_MEDIA_QUERY_PLATFORMS;
const OutbrainWidget: React.FC = () => {
	const { outbrain, shouldRenderWidget } = useOutbrain();
	const canonicalLink = document.querySelector<HTMLLinkElement>("link[rel='canonical']");
	const canonicalURL = canonicalLink?.href ?? window.location.href;
	const { currentMediaQuery: platform } = useResponsiveContext();
	const isMobile = platform === MOBILE || platform === MOBILE_LANDSCAPE || platform === TABLET;
	const widgetId = isMobile ? outbrain?.mobileWidgetId : outbrain?.desktopWidgetId;

	React.useEffect(() => {
		let isMounted = true;
		let retryCount = 0;

		async function loadOutbrain() {
			if (!shouldRenderWidget) return;

			const obr = (window as any).OBR;

			if (obr?.extern?.reloadWidget) {
				obr.extern.reloadWidget();
				await new Promise(resolve => setTimeout(resolve, 1000));
				await retryRefresh();
			} else if (!IS_PROD) {
				setupMockOutbrain();
			}
		}

		async function retryRefresh() {
			if (!isMounted || document.getElementById(CONTAINER_ID)?.children?.length || retryCount >= 5) return;

			(window as any).OBR?.extern.refreshWidget(canonicalURL);

			await new Promise(resolve => setTimeout(resolve, 500 * 2 ** retryCount));
			if (!isMounted) return;

			retryCount += 1;
			await retryRefresh();
		}

		loadOutbrain();

		return () => {
			isMounted = false;
		};
	}, [shouldRenderWidget, canonicalURL]);

	if (!shouldRenderWidget) {
		return null;
	}

	return (
		// CONTAINER_ID is replaced with new DIV id in runtime
		// other part of application ("Credit" component) may need a way to find widget container:
		//
		// CONTAINER_POSITION_TOP_OFFSET_EL_ID is a helper for IntersectionObserver inside
		// useOutbrain hook. It's used to calculate widget position on the page (with predefined offset).
		<div id={CONTAINER_WRAP_ID} className={cn(css.outbrainWidget, isMobile === false && css.desktopPlatform)}>
			<div id={CONTAINER_POSITION_TOP_OFFSET_EL_ID} className={css.outbrainWidgetTopOffset} />
			<div className="OUTBRAIN" data-src={canonicalURL} data-widget-id={widgetId} id={CONTAINER_ID} />
		</div>
	);
};

/**
 * Sets up a mock Outbrain widget for testing purposes.
 * This function creates a simple widget with links that
 * appear when the widget is in the viewport.
 */
function setupMockOutbrain() {
	const el = document.getElementById(CONTAINER_ID);
	if (!el) return;

	const style = `padding: 12px; border: 1px dashed #ccc; background: #fafafa;display: grid; grid-auto-rows: 200px;`;
	const observer = new IntersectionObserver(
		([entry]) => {
			if (entry.isIntersecting && !el.dataset.loaded) {
				el.innerHTML = `
						<div style="${style}">
							<strong>Mock Outbrain Widget</strong>
							<a href="#">Top 10 Programming Languages in 2025</a>
						    <a href="#">How to Prevent Developer Burnout</a>
						    <a href="#">AI Tools That Boost Productivity</a>
						    <a href="#">The Future of Web Development</a>
						</div>
					`;
				el.dataset.loaded = 'true';
				observer.unobserve(el);
			}
		},
		{ threshold: 0.5 }
	);

	observer.observe(el);
}

export default OutbrainWidget;
